import type { ConsoleTraceRun, ConsoleTraceToolCall } from '@plato/shared';
import { DataTable } from '../../components/data-table.tsx';
import { Field } from '../../components/field.tsx';
import { formatCount, formatDateTime } from '../../components/format.ts';
import { Json } from './message-parts.tsx';

export function RunTrace({ run }: { run: ConsoleTraceRun }) {
  const failed = run.status !== 'completed' || run.tool_calls.some(isFailedCall);
  return (
    <details className="run-trace">
      <summary>
        What the model saw{' '}
        <span className={`chip ${failed ? 'chip-danger' : ''}`}>{runLabel(run)}</span>
      </summary>
      <dl className="run-facts">
        <Field label="Model" value={run.model_id ?? 'Unknown'} />
        <Field label="Started" value={formatDateTime(run.started_at)} />
        <Field label="Took" value={duration(run)} />
        <Field label="Stopped because" value={run.stop_reason ?? '—'} />
        <Field label="Tokens in / out" value={tokens(run.usage)} />
        <Field label="Credits" value={credits(run.usage)} />
      </dl>
      <SystemPrompt value={run.system_prompt} />
      <Tools value={run.tools} />
      <details className="part-detail">
        <summary>Messages as sent ({run.messages.length})</summary>
        {run.messages.map((message, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: model messages have no ids and never reorder.
          <div key={index} className="model-message">
            <h4>{String(asRecord(message).role ?? 'message')}</h4>
            <Json value={asRecord(message).content} />
          </div>
        ))}
      </details>
      <ToolCalls calls={run.tool_calls} />
    </details>
  );
}

function SystemPrompt({ value }: { value: unknown }) {
  const messages = Array.isArray(value) ? value.map(asRecord) : null;
  return (
    <details className="part-detail">
      <summary>System prompt{messages ? ` (${messages.length} parts)` : ''}</summary>
      {messages === null ? (
        <p className="muted">Not recorded for this reply.</p>
      ) : (
        messages.map((message, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: system parts never reorder.
          <div key={index} className="model-message">
            <h4>{systemPartLabel(String(message.content ?? ''), index)}</h4>
            <Json value={message.content} />
          </div>
        ))
      )}
    </details>
  );
}

function Tools({ value }: { value: unknown }) {
  const tools = Array.isArray(asRecord(value).tools)
    ? (asRecord(value).tools as unknown[]).map(asRecord)
    : null;
  return (
    <details className="part-detail">
      <summary>Tools offered{tools ? ` (${tools.length})` : ''}</summary>
      {tools === null ? (
        <p className="muted">Not recorded for this reply.</p>
      ) : (
        <ul className="tool-list">
          {tools.map((tool) => (
            <li key={String(tool.name)}>
              <details>
                <summary>
                  <code>{String(tool.name)}</code>{' '}
                  <span className="muted">{String(tool.description ?? tool.id ?? '')}</span>
                </summary>
                <Json value={tool.inputSchema ?? tool.args} />
              </details>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function ToolCalls({ calls }: { calls: readonly ConsoleTraceToolCall[] }) {
  if (calls.length === 0) return <p className="muted">No tool calls.</p>;
  return (
    <DataTable
      caption="Tool calls"
      columns={[
        { header: 'Tool', cell: (call) => <code>{call.tool_name}</code> },
        { header: 'Source', cell: (call) => call.mcp_server ?? call.tool_kind },
        {
          header: 'Result',
          cell: (call) =>
            isFailedCall(call) ? (
              <span className="chip chip-danger">{call.error_type ?? call.status ?? 'failed'}</span>
            ) : (
              (call.status ?? 'did not finish')
            ),
        },
        {
          header: 'Took',
          cell: (call) => (call.duration_ms === null ? '—' : `${formatCount(call.duration_ms)} ms`),
        },
        {
          header: 'Arguments',
          cell: (call) => (
            <details>
              <summary>Show</summary>
              <Json value={call.arguments} />
            </details>
          ),
        },
      ]}
      rows={calls.map((call) => ({ ...call, id: call.tool_call_id }))}
    />
  );
}

const isFailedCall = (call: ConsoleTraceToolCall) =>
  call.status !== null && call.status !== 'ok' && call.status !== 'aborted';

function runLabel(run: ConsoleTraceRun): string {
  const failures = run.tool_calls.filter(isFailedCall).length;
  if (run.status !== 'completed') return run.status;
  return failures > 0
    ? `${failures} failed tool ${failures === 1 ? 'call' : 'calls'}`
    : 'completed';
}

function systemPartLabel(content: string, index: number): string {
  if (content.includes('<vendor_instructions>')) return 'Your product and instructions';
  return index === 0 ? 'Plato platform prompt' : `System part ${index + 1}`;
}

function duration(run: ConsoleTraceRun): string {
  if (run.ended_at === null) return 'Still running or interrupted';
  const ms = new Date(run.ended_at).getTime() - new Date(run.started_at).getTime();
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function tokens(usage: ConsoleTraceRun['usage']): string {
  if (usage === null) return '—';
  const input = Number(usage.inputTokens ?? 0);
  const output = Number(usage.outputTokens ?? 0);
  return `${formatCount(input)} / ${formatCount(output)}`;
}

function credits(usage: ConsoleTraceRun['usage']): string {
  const micros = usage?.creditMicros;
  if (typeof micros !== 'number') return '—';
  return (micros / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}
